import {Box, Button, Card, CTABanner, FAQ, FAQGroup, Grid, Section, SectionIntro, Text} from '@primer/react-brand/esm'
import {getPrimerIcon} from '@github-ui/swp-core/lib/utils/icons'
import {documentToReactComponents} from '@contentful/rich-text-react-renderer'
import {renderWithoutParagraphs, renderWithSpans} from '../../../utils'
import type {OutroSectionProps} from './types'
import baseStyles from '../../../styles/base.module.css'
import {BrandedDivider} from '../../../components/BrandedDivider'
import {
  FlexSuiteTestimonialBanner,
  FlexSuiteTestimonialQuote,
  FlexSuiteTestimonialStat,
} from '../../../components/FlexSuiteTestimonial'

export const OutroSection = ({
  settings,
  outroCustomerStories,
  outroCtaBanner,
  outroCtaHeading,
  outroCtaDescription,
  outroCtaOne,
  outroCtaTwo,
  outroIncludeResources,
  outroResourcesHeading,
  outroResources,
  outroFaqs,
  outroHeading,
  outroTestimonial,
  outroTestimonialLink,
  outroTestimonialLogo,
  outroTestimonialQuote,
  outroTestimonialImage,
  outroTestimonialLabel,
  outroTestimonialStats,
  outroTestimonialName,
  outroTestimonialTitle,
}: OutroSectionProps) => {
  const brand = settings?.fields.brand
  const validFaqGroups = outroFaqs?.filter(group =>
    group.fields.faqs.some(faq => faq.fields.question && faq.fields.answer),
  )
  const singleFaqGroup = validFaqGroups && validFaqGroups.length === 1 ? validFaqGroups[0] : null

  const hasCustomerStories = outroCustomerStories && outroCustomerStories.length > 0
  const hasTestimonial =
    (outroTestimonial === 'Quote' && Boolean(outroTestimonialQuote)) ||
    (outroTestimonial === 'Stats' &&
      Boolean(
        outroTestimonialStats?.length && outroTestimonialQuote && outroTestimonialLink && outroTestimonialImage,
      )) ||
    (outroTestimonial === 'Brand Banner' && Boolean(outroTestimonialQuote))

  if (
    !hasTestimonial &&
    !hasCustomerStories &&
    !outroCtaBanner &&
    (!outroIncludeResources || !outroResources?.length) &&
    (!validFaqGroups || validFaqGroups.length === 0)
  ) {
    return null
  }

  return (
    <Box marginBlockStart={48} className={baseStyles.greenBorderBottom}>
      <Section paddingBlockStart="none" className={baseStyles.gridFrameTop} paddingBlockEnd="none" fullWidth>
        {hasCustomerStories && (
          <Box className={`${baseStyles.gridFrameTop} ${baseStyles.gridFrameBottom}`}>
            <Grid columnGap="none" className={baseStyles.gridContent}>
              {outroCustomerStories.map(story => {
                const Octicon = getPrimerIcon(story.fields.icon)
                return (
                  <Grid.Column key={story.sys.id} span={{medium: 4}} className="gridColumn">
                    <Box
                      className={baseStyles.gridItem}
                      padding={{narrow: 24, regular: 40, wide: 64}}
                      paddingBlockStart={{regular: 80}}
                      paddingBlockEnd={{regular: 80}}
                      borderWidth="thin"
                      borderColor="muted"
                      style={{height: '100%'}}
                    >
                      <Card
                        href={story.fields.href}
                        variant="minimal"
                        fullWidth
                        ctaVariant="arrow"
                        ctaText={story.fields.label}
                      >
                        {Octicon && <Card.Icon icon={<Octicon />} color="default" hasBackground />}
                        {story.fields.eyebrow ? <Card.Label>{story.fields.eyebrow}</Card.Label> : null}
                        <Card.Heading>
                          {renderWithoutParagraphs(story.fields.heading)}
                          {story.fields.alt ? <span className="sr-only"> {story.fields.alt}</span> : null}
                        </Card.Heading>
                        <Card.Description>{renderWithoutParagraphs(story.fields.description)}</Card.Description>
                      </Card>
                    </Box>
                  </Grid.Column>
                )
              })}
            </Grid>
          </Box>
        )}

        {outroTestimonial === 'Quote' && outroTestimonialQuote ? (
          <Box paddingBlockEnd="spacious" paddingBlockStart="spacious">
            <Grid>
              <Grid.Column>
                <FlexSuiteTestimonialQuote
                  quote={outroTestimonialQuote}
                  name={outroTestimonialName}
                  title={outroTestimonialTitle}
                  link={outroTestimonialLink && outroTestimonialLink.fields}
                  logo={outroTestimonialLogo}
                />
              </Grid.Column>
            </Grid>
          </Box>
        ) : null}

        {outroTestimonial === 'Stats' &&
        outroTestimonialStats &&
        outroTestimonialQuote &&
        outroTestimonialLink &&
        outroTestimonialImage ? (
          <Box marginBlockStart={48}>
            <FlexSuiteTestimonialStat
              quote={outroTestimonialQuote}
              link={outroTestimonialLink.fields}
              label={outroTestimonialLabel}
              image={outroTestimonialImage}
              stats={outroTestimonialStats}
            />
          </Box>
        ) : null}

        {outroTestimonial === 'Brand Banner' && outroTestimonialQuote ? (
          <Box paddingBlockEnd="spacious" paddingBlockStart="spacious">
            <FlexSuiteTestimonialBanner
              quote={outroTestimonialQuote}
              link={outroTestimonialLink && outroTestimonialLink.fields}
              logo={outroTestimonialLogo}
              image={outroTestimonialImage}
            />
          </Box>
        ) : null}

        {outroCtaBanner && (
          <Box paddingBlockStart={{narrow: 'spacious', regular: 64}}>
            <CTABanner>
              {outroCtaHeading && <CTABanner.Heading>{renderWithoutParagraphs(outroCtaHeading)}</CTABanner.Heading>}
              {outroCtaDescription && (
                <CTABanner.Description>{renderWithoutParagraphs(outroCtaDescription)}</CTABanner.Description>
              )}
              {outroCtaOne?.fields.href && outroCtaTwo?.fields.href ? (
                <CTABanner.ButtonGroup>
                  <Button as="a" href={outroCtaOne.fields.href}>
                    {outroCtaOne.fields.text}
                  </Button>
                  <Button as="a" href={outroCtaTwo.fields.href} variant="secondary">
                    {outroCtaTwo.fields.text}
                  </Button>
                </CTABanner.ButtonGroup>
              ) : outroCtaOne?.fields.href ? (
                <CTABanner.ButtonGroup>
                  <Button as="a" href={outroCtaOne.fields.href}>
                    {outroCtaOne.fields.text}
                  </Button>
                </CTABanner.ButtonGroup>
              ) : null}
            </CTABanner>
          </Box>
        )}

        {brand === 'Security' ? (
          <Box className={baseStyles.gridFrameBottom}>
            <Grid>
              <Grid.Column>
                <BrandedDivider brand={brand} />
              </Grid.Column>
            </Grid>
          </Box>
        ) : null}

        {outroIncludeResources && (
          <>
            {outroResourcesHeading && (
              <Box
                paddingBlockEnd={{
                  narrow: 'spacious',
                  regular: 64,
                }}
                paddingBlockStart={{
                  narrow: 'spacious',
                  regular: 64,
                }}
              >
                <SectionIntro align="center" fullWidth>
                  <SectionIntro.Heading size="3">{renderWithoutParagraphs(outroResourcesHeading)}</SectionIntro.Heading>
                </SectionIntro>
              </Box>
            )}
            <Box className={`${baseStyles.gridFrameTop} ${baseStyles.gridFrameBottom}`}>
              <Grid columnGap="none" className={baseStyles.gridContent}>
                {outroResources?.map(resource => {
                  const Octicon = getPrimerIcon(resource.fields.icon)
                  return (
                    <Grid.Column key={resource.sys.id} span={{medium: 4}} className="gridColumn">
                      <Box
                        className={baseStyles.gridItem}
                        padding={{narrow: 24, regular: 40, wide: 64}}
                        paddingBlockStart={{regular: 80}}
                        paddingBlockEnd={{regular: 80}}
                        borderWidth="thin"
                        borderColor="muted"
                        style={{height: '100%'}}
                      >
                        <Card
                          href={resource.fields.href}
                          variant="minimal"
                          fullWidth
                          ctaVariant="arrow"
                          ctaText={resource.fields.label}
                        >
                          {Octicon && <Card.Icon icon={<Octicon />} color="default" hasBackground />}
                          {resource.fields.eyebrow ? <Card.Label>{resource.fields.eyebrow}</Card.Label> : null}
                          <Card.Heading>
                            {renderWithoutParagraphs(resource.fields.heading)}
                            {resource.fields.alt ? <span className="sr-only"> {resource.fields.alt}</span> : null}
                          </Card.Heading>
                          <Card.Description>{renderWithoutParagraphs(resource.fields.description)}</Card.Description>
                        </Card>
                      </Box>
                    </Grid.Column>
                  )
                })}
              </Grid>
            </Box>
          </>
        )}

        {validFaqGroups && validFaqGroups.length > 1 && (
          <Box paddingBlockStart={outroHeading ? undefined : {narrow: 'spacious', regular: 64}}>
            <FAQGroup variant="gridline" defaultSelectedIndex={0}>
              {outroHeading && (
                <FAQGroup.Heading>
                  <Box paddingBlockStart={{narrow: 'spacious', regular: 64}} paddingInlineStart="spacious">
                    {renderWithSpans(outroHeading, 'd-block')}
                  </Box>
                </FAQGroup.Heading>
              )}

              {validFaqGroups.map(group => {
                const completeFaqs = group.fields.faqs.filter(faq => faq.fields.question && faq.fields.answer)

                return (
                  <FAQ key={group.sys.id} variant="gridline">
                    {group.fields.heading && <FAQ.Heading>{renderWithoutParagraphs(group.fields.heading)}</FAQ.Heading>}
                    {completeFaqs.map((faq, index) => (
                      <FAQ.Item key={faq.sys.id} name={`faq-${group.sys.id}`} open={index === 0}>
                        <FAQ.Question>{faq.fields.question}</FAQ.Question>
                        <FAQ.Answer>
                          <Text as="div" variant="muted">
                            {documentToReactComponents(faq.fields.answer)}
                          </Text>
                        </FAQ.Answer>
                      </FAQ.Item>
                    ))}
                  </FAQ>
                )
              })}
            </FAQGroup>
          </Box>
        )}

        {singleFaqGroup && (
          <Box paddingBlockStart={outroHeading ? undefined : {narrow: 'spacious', regular: 64}}>
            <FAQ key={singleFaqGroup.sys.id} variant="gridline">
              {outroHeading && (
                <FAQ.Heading>
                  <Box paddingBlockStart={{narrow: 'spacious', regular: 64}} paddingInlineStart="spacious">
                    {renderWithSpans(outroHeading, 'd-block')}
                  </Box>
                </FAQ.Heading>
              )}
              {!outroHeading && singleFaqGroup.fields.heading && (
                <FAQ.Heading>{renderWithoutParagraphs(singleFaqGroup.fields.heading)}</FAQ.Heading>
              )}
              {singleFaqGroup.fields.faqs
                .filter(faq => faq.fields.question && faq.fields.answer)
                .map((faq, index) => (
                  <FAQ.Item key={faq.sys.id} name={`faq-${singleFaqGroup.sys.id}`} open={index === 0}>
                    <FAQ.Question>{faq.fields.question}</FAQ.Question>
                    <FAQ.Answer>
                      <Text as="div" variant="muted">
                        {documentToReactComponents(faq.fields.answer)}
                      </Text>
                    </FAQ.Answer>
                  </FAQ.Item>
                ))}
            </FAQ>
          </Box>
        )}
      </Section>
    </Box>
  )
}
